import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api, brl, img, effPrice, track } from "@/lib/api";

export const NAV = [
  ["Início", "/"], ["Gamer", "/produtos?categoria=gamer"], ["Geek", "/produtos?categoria=geek"], ["Decoração", "/produtos?categoria=decoracao"],
  ["Casa & Organização", "/produtos?categoria=casa-organizacao"], ["Personalizados", "/personalizados"], ["Empresas", "/empresas"], ["Todos os Produtos", "/produtos"],
];

export const Logo = ({ className = "h-10" }) => <img src="/brand/logo-sm.png" alt="E³ Impressão 3D" className={`${className} w-auto object-contain`} />;

function SearchBox({ onDone }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) {
      setRes([]);
      return;
    }
    const t = setTimeout(() => api.get("/search/suggest", { params: { q } }).then((r) => setRes(r.data)), 220);
    return () => clearTimeout(t);
  }, [q]);
  const submit = (e) => {
    e.preventDefault();
    track("Search", { search_string: q });
    nav(`/produtos?q=${encodeURIComponent(q)}`);
    onDone?.();
  };
  return (
    <form onSubmit={submit} className="relative w-full" role="search">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A0A0A0]" />
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto, SKU, categoria..." aria-label="Buscar" className="input pl-11" data-testid="search-input" />
      {res.length > 0 && (
        <div className="absolute z-50 mt-2 w-full card p-2 shadow-2xl" data-testid="search-suggestions">
          {res.map((p) => (
            <Link key={p.slug} to={`/produto/${p.slug}`} onClick={() => { setQ(""); onDone?.(); }} className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#1f232b]" data-testid={`suggest-${p.slug}`}>
              <img src={img(p.images?.[0])} alt={p.name} className="h-11 w-11 rounded-lg object-cover" />
              <span className="flex-1 text-sm">{p.name}</span>
              <span className="text-sm font-semibold text-[#00C8FF]">{brl(effPrice(p))}</span>
            </Link>
          ))}
        </div>
      )}
    </form>
  );
}

export default function Header() {
  const { cartCount, favorites, customer, setCartOpen } = useStore();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const iconBtn = "relative grid place-items-center h-11 w-11 rounded-xl hover:bg-[#171A1F] transition-colors";
  return (
    <>
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050608]/85 border-b border-[#282D37]/60 no-print" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 h-[72px] flex items-center gap-4">
        <button className={`${iconBtn} xl:hidden`} onClick={() => setMenu(true)} aria-label="Abrir menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <Link to="/" data-testid="header-logo" className="shrink-0"><Logo className="h-12 lg:h-14" /></Link>
        <nav className="hidden xl:flex flex-1 justify-center gap-1" aria-label="Principal">
          {NAV.map(([l, to]) => (
            <NavLink key={to} to={to} end className="px-3 py-2 text-[13px] font-semibold text-[#E6E6E6] hover:text-[#00C8FF] transition-colors whitespace-nowrap" data-testid={`nav-${l}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="ml-auto xl:ml-0 flex items-center gap-1">
          <button className={iconBtn} onClick={() => setSearch((s) => !s)} aria-label="Buscar" data-testid="header-search-btn"><Search className="h-5 w-5" /></button>
          <Link to="/minha-conta" className={`${iconBtn} hidden sm:grid`} aria-label="Minha conta" data-testid="header-account-btn"><User className={`h-5 w-5 ${customer ? "text-[#00C8FF]" : ""}`} /></Link>
          <Link to="/favoritos" className={`${iconBtn} hidden sm:grid`} aria-label="Favoritos" data-testid="header-favorites-btn">
            <Heart className="h-5 w-5" />
            {favorites.length > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#00C8FF]" />}
          </Link>
          <button className={iconBtn} onClick={() => setCartOpen(true)} aria-label="Carrinho" data-testid="header-cart-btn">
            <ShoppingBag className="h-5 w-5" />
            {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#008CFF] text-[11px] font-bold grid place-items-center" data-testid="cart-badge">{cartCount}</span>}
          </button>
        </div>
      </div>
      {search && <div className="max-w-2xl mx-auto px-4 pb-4"><SearchBox onDone={() => setSearch(false)} /></div>}
    </header>
      {menu && (
        <div className="fixed inset-0 z-50 xl:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMenu(false)} />
          <aside className="absolute left-0 top-0 h-full w-[86%] max-w-sm bg-[#0B0D11] border-r border-[#282D37] p-6 flex flex-col rise">
            <div className="flex items-center justify-between mb-8">
              <Logo className="h-10" />
              <button onClick={() => setMenu(false)} className={iconBtn} aria-label="Fechar menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map(([l, to]) => (
                <Link key={to} to={to} onClick={() => setMenu(false)} className="py-3.5 px-3 rounded-xl text-base font-semibold hover:bg-[#171A1F]" data-testid={`mobile-nav-${l}`}>{l}</Link>
              ))}
            </nav>
            <div className="mt-auto grid grid-cols-2 gap-3 pt-6">
              <Link to="/minha-conta" onClick={() => setMenu(false)} className="btn-ghost"><User className="h-4 w-4" />Conta</Link>
              <Link to="/favoritos" onClick={() => setMenu(false)} className="btn-ghost"><Heart className="h-4 w-4" />Favoritos</Link>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
